<template>
  <vxe-grid
    ref="table"
    size="mini"
    row-id="shipmentGoodsCode"
    border
    resizable
    auto-resize
    show-overflow
    highlight-hover-row
    :data="displayData"
    :columns="tableColumns"
    :loading="loading"
    :height="tableHeight"
    :row-style="rowStyleMethod"
    :cell-class-name="cellClassName"
    :span-method="spanMethod"
    :checkbox-config="{ highlight: true, reserve: true, range: true }"
    :scroll-y="{ enabled: true, gt: 0 }"
    @checkbox-change="checkboxChange"
    @checkbox-all="checkboxAll"
    @checkbox-range-change="checkboxChange"
  >
    <template #seqno="{ seq, row }">
      {{ row._seq || seq }}
    </template>

    <template #remainCtnQty="{ row }">
      <template v-if="row._isGroup">
        <div class="group-row-content" @click="toggleGroup(row._groupKey)">
          <i
            class="el-icon-arrow-right group-toggle-icon"
            :class="{ 'is-expanded': expandedGroups[row._groupKey] === true }"
          ></i>
          <span class="group-row-main">{{ row.groupLabel }}</span>
          <span class="group-row-meta">
            <span class="group-row-count">
              {{ row.groupCount }}{{ $l('label_item_count') || '项' }}
            </span>
            <span class="group-row-divider">|</span>
            <span class="group-row-vol">{{ row.groupVolume }}m³</span>
          </span>
        </div>
      </template>
      <span v-else class="pool-qty">{{ row.remainCtnQty }}</span>
    </template>

    <template #opt="{ row }">
      <div v-if="row._isGroup">
        <el-button
          type="primary"
          plain
          size="mini"
          :disabled="row.groupRemainCtnQty <= 0"
          class="px-2"
          @click="$emit('group-assign', row.children)"
        >
          <i class="el-icon-plus"></i>
          {{ $l('btn_group_assign_short') || '整组' }}
        </el-button>
      </div>
      <div
        v-else
        class="pool-assign-btn"
        :class="{ 'is-disabled': row.remainCtnQty <= 0 }"
        @click="row.remainCtnQty > 0 && $emit('assign', row)"
      >
        <i class="el-icon-plus"></i>
      </div>
    </template>

    <template #planNoHeader>
      <filter-content
        :text="$l('form_label_planNos') || '补货计划编号'"
        :items="options.planNo"
        @filter="(e) => (filters.planNo = e)"
        submitFilterOnClose
      />
    </template>
    <template #fbaDcHeader>
      <filter-content
        :text="$l('table_label_fbaDc') || 'FBA DC'"
        :items="options.fbaDc"
        @filter="(e) => (filters.fbaDc = e)"
        submitFilterOnClose
      />
    </template>
    <template #fbaIdHeader>
      <filter-content
        :text="$l('table_label_fbaId') || 'FBA ID'"
        :items="options.fbaId"
        @filter="(e) => (filters.fbaId = e)"
        submitFilterOnClose
      />
    </template>
    <template #amzSkuNoHeader>
      <filter-content
        :text="$l('table_label_sku') || 'SKU'"
        :items="options.amzSkuNo"
        @filter="(e) => (filters.amzSkuNo = e)"
        submitFilterOnClose
      />
    </template>
    <template #amzStoreSkuNoHeader>
      <filter-content
        :text="$l('table_label_storeSku') || '店铺 SKU'"
        :items="options.amzStoreSkuNo"
        @filter="(e) => (filters.amzStoreSkuNo = e)"
        submitFilterOnClose
      />
    </template>
    <template #shippingFactoryNameHeader>
      <filter-content
        :text="$l('table_label_shippingFactoryName') || '送货工厂'"
        :items="options.shippingFactoryName"
        @filter="(e) => (filters.shippingFactoryName = e)"
        submitFilterOnClose
      />
    </template>
    <template #goodsNameHeader>
      <filter-content
        :text="$l('table_label_goodsName') || '产品名称'"
        :items="options.goodsName"
        @filter="(e) => (filters.goodsName = e)"
        submitFilterOnClose
      />
    </template>
    <template #goodsColorHeader>
      <filter-content
        :text="$l('table_label_goodsColor') || '颜色'"
        :items="options.goodsColor"
        @filter="(e) => (filters.goodsColor = e)"
        submitFilterOnClose
      />
    </template>
  </vxe-grid>
</template>

<script>
import lodash from 'lodash'
import { add, multiply, round } from '@/libs/util.number'
import {
  applyTableFilters,
  buildFilterOptions,
  resetTableFilters,
} from '../table-filter-helpers'
import TableAutoHeight from '@/mixins/table-auto-height'
import tableColumns from './table-columns'
import {
  buildPendingGoodsPoolColumns,
  getPendingGoodsPoolGroupSpan,
  getPendingGoodsPoolGroupRemainQty,
} from './grouping-helpers'
import {
  getCheckedCodeMap,
  getCheckedRows,
  isGroupChecked,
  updateCheckedCodes,
} from '../group-checkbox-helpers'

export default {
  mixins: [TableAutoHeight],
  props: {
    tableData: { type: Array, default: () => [] },
    groupFields: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      tableColumns: [],
      expandedGroups: {},
      checkedGoodsCodes: [],
      filters: {
        planNo: [],
        fbaDc: [],
        fbaId: [],
        amzSkuNo: [],
        amzStoreSkuNo: [],
        shippingFactoryName: [],
        goodsName: [],
        goodsColor: [],
      },
    }
  },
  created() {
    this._baseColumns = tableColumns(this)
    this.updateTableColumns()
  },
  mounted() {
    this.$emit('group-state-change', this.hasGroups())
  },
  computed: {
    options() {
      return buildFilterOptions(this.tableData, Object.keys(this.filters))
    },
    filteredRows() {
      return applyTableFilters(this.tableData, this.filters, this.options)
    },
    displayData() {
      if (!this.groupFields.length) return this.filteredRows

      const emptyLabel = this.$l('INDEX.label_empty_value') || '(空)'
      const groups = {}
      const groupOrder = []
      const sortedRows = lodash.orderBy(
        this.filteredRows,
        this.groupFields.map((field) => (row) => {
          const value = row[field]
          return lodash.isUndefined(value) ||
            lodash.isNull(value) ||
            value === ''
            ? ''
            : value
        })
      )

      sortedRows.forEach((row) => {
        const groupValues = this.groupFields.map((field) => {
          const value = row[field]
          return lodash.isUndefined(value) ||
            lodash.isNull(value) ||
            value === ''
            ? null
            : value
        })
        const key = JSON.stringify(groupValues)
        if (!groups[key]) {
          groups[key] = {
            key,
            label: groupValues
              .map((value) => (value === null ? emptyLabel : value))
              .join(' · '),
            rows: [],
          }
          groupOrder.push(key)
        }
        groups[key].rows.push(row)
      })

      const result = []
      groupOrder.forEach((key, index) => {
        const group = groups[key]
        const groupRemainCtnQty = getPendingGoodsPoolGroupRemainQty(group.rows)
        const groupVolume = group.rows.reduce(
          (sum, item) =>
            add(
              sum,
              item.ctnVolume && item.remainCtnQty
                ? multiply(item.ctnVolume, item.remainCtnQty)
                : item.totalVolume || 0
            ),
          0
        )
        const groupSeq = index + 1
        result.push({
          _isGroup: true,
          shipmentGoodsCode: `group-${index}`,
          _groupKey: key,
          _seq: String(groupSeq),
          groupLabel: group.label,
          groupCount: group.rows.length,
          groupRemainCtnQty,
          groupVolume: round(groupVolume, 2),
          remainCtnQty: '',
          children: group.rows,
        })
        if (this.expandedGroups[key] === true) {
          group.rows.forEach((r, childIdx) =>
            result.push({ ...r, _seq: `${groupSeq}.${childIdx + 1}` })
          )
        }
      })
      return result
    },
  },
  watch: {
    groupFields(newVal, oldVal) {
      if (!!newVal.length !== !!oldVal.length) {
        this.updateTableColumns()
      }
      this.clearCheckboxRow()
      this.expandedGroups = {}
      this.$emit('expand-state-change', false)
      this.$emit('group-state-change', this.hasGroups())
    },
    tableData() {
      this.resetFilters()
      this.clearCheckboxRow()
      this.expandedGroups = {}
      this.$emit('expand-state-change', false)
      this.$emit('group-state-change', this.hasGroups())
    },
    filters: {
      handler() {
        this.clearCheckboxRow()
        this.expandedGroups = {}
        this.$emit('expand-state-change', false)
        this.$emit('group-state-change', this.hasGroups())
      },
      deep: true,
    },
  },
  methods: {
    resetFilters() {
      this.filters = resetTableFilters(this.filters)
    },
    toggleGroup(key) {
      const isExpanded = this.expandedGroups[key] === true
      this.$set(this.expandedGroups, key, !isExpanded)
      this.$nextTick(async () => {
        await this.syncGroupRowsChecked()
        this.$emit('expand-state-change', this.isAllGroupsExpanded())
      })
    },
    expandAllGroups() {
      this.setAllGroupsExpanded(true)
    },
    collapseAllGroups() {
      this.setAllGroupsExpanded(false)
    },
    toggleAllGroups() {
      if (this.isAllGroupsExpanded()) {
        this.collapseAllGroups()
        return
      }

      this.expandAllGroups()
    },
    setAllGroupsExpanded(expanded) {
      const groupKeys = this.getGroupKeys()

      if (!this.groupFields.length || !groupKeys.length) {
        this.expandedGroups = {}
        this.$emit('expand-state-change', false)
        this.$emit('group-state-change', false)
        return
      }

      const nextExpandedGroups = {}
      groupKeys.forEach((key) => {
        nextExpandedGroups[key] = expanded
      })
      this.expandedGroups = nextExpandedGroups

      this.$nextTick(async () => {
        await this.syncGroupRowsChecked()
        this.$emit('expand-state-change', this.isAllGroupsExpanded())
        this.$emit('group-state-change', true)
      })
    },
    getGroupKeys() {
      if (!this.groupFields.length) return []

      return this.displayData
        .filter((row) => row._isGroup && row._groupKey)
        .map((row) => row._groupKey)
    },
    hasGroups() {
      return !!this.getGroupKeys().length
    },
    isAllGroupsExpanded() {
      const groupKeys = this.getGroupKeys()

      return (
        groupKeys.length > 0 &&
        groupKeys.every((key) => this.expandedGroups[key] === true)
      )
    },
    updateTableColumns() {
      this.tableColumns = buildPendingGoodsPoolColumns(
        this._baseColumns,
        !!this.groupFields.length
      )
    },
    rowStyleMethod({ row }) {
      if (!row._isGroup) return null

      return {
        backgroundColor: '#f8fafc',
        fontWeight: '600',
      }
    },
    cellClassName({ row, column }) {
      if (!row._isGroup) return ''

      if (column.type === 'seq') return 'is-group-row-cell'
      if (column.property === 'remainCtnQty')
        return 'is-group-row-cell is-group-row-merged'

      return 'is-group-row-cell'
    },
    spanMethod(args) {
      return getPendingGoodsPoolGroupSpan({
        ...args,
        columns: this.tableColumns,
      })
    },
    async checkboxChange({ row, checked, records = [], reserves = [] } = {}) {
      const $table = this.$refs.table
      if (!$table) return

      if (row) {
        this.checkedGoodsCodes = updateCheckedCodes(
          this.checkedGoodsCodes,
          row._isGroup ? row.children : [row],
          'shipmentGoodsCode',
          checked
        )
      } else {
        const rows = records.concat(reserves).reduce((result, item) => {
          return result.concat(item && item._isGroup ? item.children : [item])
        }, [])
        this.checkedGoodsCodes = updateCheckedCodes(
          [],
          rows,
          'shipmentGoodsCode',
          true
        )
      }

      await this.syncGroupRowsChecked()
      this.emitCheckboxChange()
    },
    async checkboxAll({ checked }) {
      const $table = this.$refs.table
      if (!$table) return

      if (!checked) {
        await this.clearCheckboxRow()
        return
      }

      this.checkedGoodsCodes = updateCheckedCodes(
        [],
        this.filteredRows,
        'shipmentGoodsCode',
        true
      )
      await this.syncGroupRowsChecked()
      this.emitCheckboxChange()
    },
    getCheckedGoodsRows() {
      return getCheckedRows(
        this.filteredRows,
        'shipmentGoodsCode',
        this.checkedGoodsCodes
      )
    },
    async syncGroupRowsChecked() {
      if (!this.groupFields.length || !this.$refs.table) return

      const checkedCodeMap = getCheckedCodeMap(this.checkedGoodsCodes)
      const goodsRows = this.displayData.filter(
        (row) => !row._isGroup && row.shipmentGoodsCode
      )
      const groupRows = this.displayData.filter((row) => row._isGroup)

      await Promise.all(
        goodsRows.map((row) =>
          this.$refs.table.setCheckboxRow(
            row,
            !!checkedCodeMap[row.shipmentGoodsCode]
          )
        )
      )
      await Promise.all(
        groupRows.map((row) => {
          return this.$refs.table.setCheckboxRow(
            row,
            isGroupChecked(row, 'shipmentGoodsCode', checkedCodeMap)
          )
        })
      )
    },
    emitCheckboxChange() {
      this.$emit('checkbox-change', this.getCheckedGoodsRows())
    },
    async clearCheckboxRow() {
      const $table = this.$refs.table
      this.checkedGoodsCodes = []
      if ($table) {
        await $table.clearCheckboxRow()
        await $table.clearCheckboxReserve()
      }
      this.$emit('checkbox-change', [])
    },
    getHeight() {
      if (!this.tableRef || !this.tableRef.offsetHeight) return

      setTimeout(() => {
        const container = this.$el.parentElement
        if (!container) return

        const { bottom = 0 } = container.getBoundingClientRect() || {}
        const { top = 0 } = this.tableRef.getBoundingClientRect() || {}
        this.tableHeight = bottom - top
      }, 200)
    },
  },
}
</script>

<style lang="scss" scoped>
.pool-qty {
  color: #409eff;
  font-weight: 600;
  font-size: 13px;
}

.pool-assign-btn {
  width: 24px;
  height: 24px;
  border-radius: 5px;
  border: 1px solid rgba(64, 158, 255, 0.2);
  background: rgba(64, 158, 255, 0.1);
  color: #409eff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  i {
    line-height: initial;
  }
}

.pool-assign-btn:hover {
  background: rgba(64, 158, 255, 0.2);
  border-color: rgba(64, 158, 255, 0.35);
}

.pool-assign-btn.is-disabled {
  opacity: 0.4;
  cursor: not-allowed;
  &:hover {
    background: rgba(64, 158, 255, 0.1);
    border-color: rgba(64, 158, 255, 0.2);
  }
}

.group-row-main {
  font-size: 12px;
  font-weight: 600;
  color: #1e293b;
  margin-right: 8px;
}

.group-toggle-icon {
  margin-right: 8px;
  font-size: 12px;
  color: #64748b;
  transition: transform 0.15s;
  flex-shrink: 0;
  &.is-expanded {
    transform: rotate(90deg);
  }
}

.group-row-content {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  text-align: left;
  padding-left: 4px;
  cursor: pointer;
}

.group-row-meta {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 500;
}

.group-row-count {
  color: #64748b;
  background: #e2e8f0;
  padding: 0 6px;
  border-radius: 8px;
}

.group-row-vol {
  color: #3b82f6;
}

.group-row-divider {
  color: #e2e8f0;
}

::v-deep .vxe-body--column.is-group-row-cell {
  background-color: #f8fafc;
}

::v-deep .vxe-body--column.is-group-row-merged {
  box-shadow: inset 0 -1px 0 #edf2f7;
}

::v-deep .vxe-body--column.is-group-row-action {
  padding-left: 6px;
  padding-right: 6px;
}

::v-deep .vxe-body--column.is-group-row-action .vxe-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 0;
  padding-right: 0;
}
</style>
